import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PhotoGrid } from '@/components/ui/PhotoGrid';
import {
  useAct,
  useInitiateSigning,
  useSendSigningSms,
  useTenantResponse,
} from '@/hooks/useActs';
import { actsApi } from '@/api/acts';

export function ActDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const actId = id ? Number(id) : null;
  const act = useAct(actId);
  const tenantResponse = useTenantResponse(actId ?? 0);
  const initiateSigning = useInitiateSigning();
  const sendSms = useSendSigningSms();

  const [qrCode, setQrCode] = useState<string | null>(null);
  const [signingStatus, setSigningStatus] = useState<string | null>(null);
  const [expireAt, setExpireAt] = useState<Date | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const fallbackTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
    };
  }, []);

  // Poll signing status every 3s while signing is active.
  useEffect(() => {
    if (!qrCode || !act.data?.document?.id) return;
    const docId = act.data.document.id;
    const interval = window.setInterval(async () => {
      try {
        const res = await actsApi.signingStatus(docId);
        setSigningStatus(res.status);
        if (res.status !== 'waiting') {
          window.clearInterval(interval);
          void act.refetch();
        }
        if (expireAt && new Date() > expireAt) {
          window.clearInterval(interval);
        }
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => window.clearInterval(interval);
  }, [qrCode, act, expireAt]);

  if (act.isLoading) return <Spinner />;
  if (!act.data) return null;
  const a = act.data;

  const onStartSigning = async () => {
    if (!a.document?.id) return;
    const res = await initiateSigning.mutateAsync(a.document.id);
    setQrCode(res.qrCode);
    setExpireAt(new Date(res.expireAt));
    setSigningStatus('waiting');
    // Show fallback after 10 minutes if still waiting
    fallbackTimer.current = window.setTimeout(() => setShowFallback(true), 10 * 60 * 1000);
  };

  const onSendSms = async () => {
    if (!a.document?.id) return;
    await sendSms.mutateAsync(a.document.id);
  };

  return (
    <div className="space-y-4">
      <Card title={`${t(`acts.types.${a.type}`)} — #${a.id}`}>
        <div className="flex gap-2">
          <Badge tone="info">{t(`acts.status.${a.tenant_status}`)} (tenant)</Badge>
          <Badge tone="info">{t(`acts.status.${a.owner_status}`)} (owner)</Badge>
        </div>
        {a.document?.url ? (
          <iframe
            src={a.document.url}
            title="Act PDF"
            className="mt-4 h-96 w-full rounded-lg border border-slate-200"
          />
        ) : null}
      </Card>

      {a.room_conditions.length > 0 && (
        <Card title={t('acts.room_conditions')}>
          {a.room_conditions.map((rc, i) => (
            <div key={i} className="mb-4 border-b border-slate-100 pb-3">
              <p className="font-medium">{rc.room}</p>
              {rc.condition ? <Badge tone="neutral">{rc.condition}</Badge> : null}
              {rc.note ? <p className="mt-1 text-xs text-slate-600">{rc.note}</p> : null}
              <PhotoGrid photos={rc.photos.map((p) => p.url)} />
            </div>
          ))}
        </Card>
      )}

      {a.type === 'check_out' && a.tenant_status === 'pending' && (
        <Card title={t('acts.tenant_response')}>
          <TenantResponseForm
            rooms={a.room_conditions.map((r) => r.room)}
            onSubmit={(form) => tenantResponse.mutateAsync(form).then(() => void act.refetch())}
            pending={tenantResponse.isPending}
          />
        </Card>
      )}

      {a.document?.signing_status === 'pending_tenant' && (
        <Card title={t('acts.sign_title')}>
          {qrCode ? (
            <>
              <img src={qrCode} alt="QR" className="mx-auto h-48 w-48" />
              <p className="mt-3 text-center text-xs text-slate-500">
                {t('web.desktop.scan_qr_with_egov')}
              </p>
              {signingStatus === 'done' ? (
                <p className="mt-3 text-center text-green-700">{t('acts.signed_successfully')}</p>
              ) : null}
              {showFallback && (
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <p className="mb-2 text-xs text-slate-500">
                    {t('acts.signing_desktop_fallback')}
                  </p>
                  <Button
                    variant="secondary"
                    onClick={() => void onSendSms()}
                    loading={sendSms.isPending}
                  >
                    {t('acts.send_sms_to_phone')}
                  </Button>
                  {sendSms.data?.phone_masked ? (
                    <p className="mt-2 text-xs text-green-700">
                      {t('acts.sms_sent_to')} {sendSms.data.phone_masked}
                    </p>
                  ) : null}
                </div>
              )}
              <p className="mt-3 text-center text-xs text-slate-400">
                {t('acts.egov_mobile_required')}
              </p>
            </>
          ) : (
            <Button onClick={() => void onStartSigning()} loading={initiateSigning.isPending}>
              {t('acts.sign_with_ecp')}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}

interface ResponseFormProps {
  rooms: string[];
  onSubmit: (form: FormData) => Promise<unknown>;
  pending: boolean;
}

function TenantResponseForm({ rooms, onSubmit, pending }: ResponseFormProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<Record<string, { condition: string; note: string }>>(() =>
    Object.fromEntries(rooms.map((r) => [r, { condition: 'good', note: '' }])),
  );
  const [photos, setPhotos] = useState<Record<string, File[]>>({});

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = new FormData();
    rooms.forEach((r, i) => {
      form.append(`room_conditions[${i}][room]`, r);
      form.append(`room_conditions[${i}][condition]`, notes[r].condition);
      form.append(`room_conditions[${i}][note]`, notes[r].note);
      (photos[r] ?? []).forEach((p) => form.append(`room_conditions[${i}][photos][]`, p));
    });
    await onSubmit(form);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {rooms.map((r) => (
        <div key={r} className="rounded-lg border border-slate-100 p-3">
          <p className="mb-2 font-medium text-sm">{r}</p>
          <select
            value={notes[r].condition}
            onChange={(e) =>
              setNotes({ ...notes, [r]: { ...notes[r], condition: e.target.value } })
            }
            className="mb-2 block w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            <option value="excellent">{t('acts.conditions.excellent')}</option>
            <option value="good">{t('acts.conditions.good')}</option>
            <option value="fair">{t('acts.conditions.fair')}</option>
            <option value="damaged">{t('acts.conditions.damaged')}</option>
          </select>
          <textarea
            placeholder={t('acts.note_placeholder')}
            value={notes[r].note}
            onChange={(e) => setNotes({ ...notes, [r]: { ...notes[r], note: e.target.value } })}
            className="mb-2 block w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          />
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setPhotos({ ...photos, [r]: Array.from(e.target.files ?? []) })}
            className="text-sm"
          />
        </div>
      ))}
      <Button type="submit" loading={pending}>
        {t('common.submit')}
      </Button>
    </form>
  );
}
